import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Center, Spinner, SimpleGrid, Text } from '@chakra-ui/react';
import {
  TableOptions,
  DataTable,
  Pagination,
  StatusBadge,
  type ToggleOption,
  type Column,
  type StatusType,
} from '@themes/aegix';
import { DrawerComponent } from '../ui/drawerComponent';
import { Button as ButtonComponent } from '@themes/aegix';
import SitesForm from '../forms/SitesForm';
import { useForm, FormProvider, Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { SiteFormInitialValues } from '../../types/SitesFormInitialValue';
import { siteFormValidationSchema } from '../../validations/sites.validation';
import type { Building, Floor, Room } from '../../services';
import { SiteDrawer } from './drawers/SiteDrawer';
import {
  useSitesFloorPlansData,
  type SiteRow,
} from './hooks/useSitesFloorPlansData';
export type { SiteRow } from './hooks/useSitesFloorPlansData';

// siteToggleOptions and columns built inside component with useMemo for i18n reactivity

export function SitesFloorPlans() {
  const { t } = useTranslation();

  const siteToggleOptions = useMemo(
    () => [
      {
        value: 'all',
        label: t('sites.toggle.all', 'All'),
        'data-e2e': 'site-filter-all',
      },
      {
        value: 'active',
        label: t('common.active', 'Active'),
        'data-e2e': 'site-filter-active',
      },
      {
        value: 'pending',
        label: t('common.pending', 'Pending'),
        'data-e2e': 'site-filter-pending',
      },
      {
        value: 'inactive',
        label: t('common.inactive', 'Inactive'),
        'data-e2e': 'site-filter-inactive',
      },
    ],
    [t],
  );

  const columns: Column<SiteRow>[] = useMemo(
    () => [
      {
        key: 'name',
        label: t('sites.col.site', 'Site'),
        sortable: true,
        filterable: true,
        width: '312px',
      },
      {
        key: 'address',
        label: t('sites.col.address', 'Address'),
        sortable: true,
        filterable: true,
        width: '280px',
      },
      {
        key: 'city',
        label: t('sites.col.city', 'City'),
        sortable: true,
        filterable: true,
        width: '180px',
      },
      {
        key: 'status',
        label: t('common.status', 'Status'),
        sortable: true,
        filterable: true,
        filterOptions: [
          { label: t('common.active', 'Active'), value: 'active' },
          { label: t('common.pending', 'Pending'), value: 'pending' },
          { label: t('common.inactive', 'Inactive'), value: 'inactive' },
        ],
        width: '120px',
        render: (row) => (
          <StatusBadge
            status={row.status}
            label={t(`common.${row.status}`, row.status)}
          />
        ),
      },
    ],
    [t],
  );

  const {
    statusFilter,
    setStatusFilter,
    searchValue,
    setSearchValue,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    sortColumn,
    sortDirection,
    filterValues,
    editOpen,
    action,
    currentRow,
    staged,
    setStaged,
    selectedSite,
    selectedBuilding,
    setSelectedBuilding,
    selectedFloor,
    setSelectedFloor,
    selectedRoom,
    setSelectedRoom,
    sites,
    filteredSites,
    totalItems,
    totalPages,
    isLoading,
    isSaving,
    error,
    formDefaultValues,
    handleFilterChange,
    handleFilterClear,
    handleSort,
    handleSubmit,
    handleDrawerClose,
    handleRowClick,
    closeBuildingDrawer,
    openAddDrawer,
  } = useSitesFloorPlansData();

  const methods = useForm<SiteFormInitialValues>({
    defaultValues: formDefaultValues,
    resolver: zodResolver(
      siteFormValidationSchema,
    ) as unknown as Resolver<SiteFormInitialValues>,
  });

  useEffect(() => {
    methods.reset(formDefaultValues);
  }, [formDefaultValues, methods]);

  return (
    <>
      <TableOptions
        toggleOptions={siteToggleOptions}
        activeToggle={statusFilter}
        onToggleChange={setStatusFilter}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        primaryAction={{
          label: t('sites.addSite', 'Add Site'),
          onClick: openAddDrawer,
        }}
      />

      <Box px={5} pb={5}>
        {isLoading ? (
          <Center py={12}>
            <Spinner size="lg" color="button.primary" />
          </Center>
        ) : error ? (
          <Center py={12}>
            <Text color="text.danger">
              {error instanceof Error
                ? error.message
                : t('sites.loadFailed', 'Failed to load sites')}
            </Text>
          </Center>
        ) : sites.length === 0 ? (
          <Center py={12}>
            <Text color="text.secondary">
              {t('sites.noSitesFound', 'No sites found')}
            </Text>
          </Center>
        ) : (
          <>
            <DataTable
              columns={columns}
              data={filteredSites}
              keyExtractor={(row) => row.id}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={handleSort}
              filterValues={filterValues}
              onFilterChange={handleFilterChange}
              onFilterClear={handleFilterClear}
              onRowClick={handleRowClick}
            />
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalItems={totalItems}
              onPageChange={setCurrentPage}
              onPageSizeChange={setPageSize}
            />
          </>
        )}
      </Box>

      {/* Site edit drawer */}
      <DrawerComponent
        isOpen={editOpen}
        onClose={() => handleDrawerClose()}
        title={
          action === 'add'
            ? t('sites.addNewSite', 'Add New Site')
            : t('sites.editSite', 'Edit Site')
        }
        e2eTitle="site-edit-drawer"
        size="sm"
        closeOnOutside={false}
        closeOnEsc={false}
        footer={
          <SimpleGrid columns={currentRow.id ? 3 : 2} gap={4} width={'100%'}>
            {currentRow.id && (
              <ButtonComponent
                visual="alert"
                onClick={() => handleDrawerClose()}
                data-e2e="delete-site-button"
              >
                {t('common.delete', 'Delete')}
              </ButtonComponent>
            )}
            <ButtonComponent
              visual="secondary"
              onClick={() => handleDrawerClose()}
              data-e2e="cancel-site-button"
            >
              {t('common.cancel', 'Cancel')}
            </ButtonComponent>
            <ButtonComponent
              visual="primary"
              type="submit"
              form="sites-form"
              id="sites-form"
              disabled={isSaving}
              data-e2e="save-site-button"
            >
              {isSaving
                ? t('common.saving', 'Saving…')
                : t('common.save', 'Save')}
            </ButtonComponent>
          </SimpleGrid>
        }
      >
        <FormProvider {...methods}>
          <form
            id="sites-form"
            onSubmit={methods.handleSubmit((values) =>
              handleSubmit(values, methods.setError),
            )}
          >
            <SitesForm staged={staged} onStagedChange={setStaged} />
          </form>
        </FormProvider>
      </DrawerComponent>

      {/* Cascading drawer: Buildings */}
      <SiteDrawer
        isOpen={!!selectedSite}
        onClose={closeBuildingDrawer}
        site={selectedSite}
        selectedBuilding={selectedBuilding}
        onSelectBuilding={setSelectedBuilding}
        selectedFloor={selectedFloor}
        onSelectFloor={setSelectedFloor}
        selectedRoom={selectedRoom}
        onSelectRoom={setSelectedRoom}
      />
    </>
  );
}
